import { Card, createStyles, Grid, Image, NavLink, Text, useMantineTheme } from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import { IconChevronRight } from '@tabler/icons'
import Link from 'next/link'
import { useRouter } from 'next/router'

import {
  Collection as CollectionType,
  Product as ProductType,
  Showcase as ShowcaseType,
  Site as SiteType,
  Store as StoreType
} from '@/types'

import ProductCard from './ProductCard'

interface Props {
  site?: SiteType
  store?: StoreType
  showcase?: ShowcaseType
  collections: CollectionType[]
  products: ProductType[]
  search?: string
}

const useStyles = createStyles((theme, { isShopper }: { isShopper: boolean }) => ({
  collectionWrapper: {
    position: 'sticky',
    top: isShopper ? '130px' : '210px'
  },
  collectionLink: {
    borderRadius: theme.radius.md,
    '&[data-active]': {
      border: `1px solid ${theme.colors[theme.primaryColor][2]}`
    },
    '&:hover': {
      backgroundColor: theme.colors.gray[1]
    }
  }
}))

export default function CollectionsDisplay({
  site,
  store,
  showcase,
  collections,
  products,
  search
}: Props) {
  const router = useRouter()
  const theme = useMantineTheme()
  const isShopper = site?.beneficiary === 'shopper'
  const { classes } = useStyles({ isShopper })
  const isMd = useMediaQuery(`(max-width: ${theme.breakpoints.md}px)`)

  // Constants
  const {
    channel: channelSlug,
    store: storeSlug,
    showcase: showcaseSlug,
    collection: collectionSlug
  } = router.query || {}
  const hasCollections = Array.isArray(collections) && !!collections.length
  const showListMode = products?.length <= 4 && !isMd
  const gridSizes = showListMode ? { span: 12 } : { span: 12, xs: 6, md: 4, lg: 3 }

  return (
    <Grid gutter="lg">
      <Grid.Col span={12} sm={4} md={3} hidden={!!search}>
        <Card className={classes.collectionWrapper}>
          {hasCollections && (
            <Text weight={700} mb="sm">
              Coleções
            </Text>
          )}
          {collections?.map(collection => (
            <NavLink
              key={collection.uid}
              label={<Text sx={{ lineHeight: 1.2 }}>{collection.displayName}</Text>}
              className={classes.collectionLink}
              icon={
                collection.image && (
                  <Image src={collection.image} alt={collection.displayName} width={32} />
                )
              }
              rightSection={<IconChevronRight size={16} stroke={1.5} />}
              active={collection.slug === collectionSlug}
              component={Link}
              href={`/${channelSlug}/${storeSlug}/${showcaseSlug}?collection=${collection.slug}`}
            />
          ))}
        </Card>
      </Grid.Col>
      <Grid.Col span={12} sm={search ? 12 : 8} md={search ? 12 : 9}>
        {Array.isArray(products) && !!products?.length && (
          <Grid gutter="lg">
            {products.map((product: any) => (
              <Grid.Col key={product?.uid} {...gridSizes}>
                <ProductCard
                  product={product}
                  site={site}
                  store={store}
                  showcase={showcase}
                  showListMode={showListMode}
                />
              </Grid.Col>
            ))}
          </Grid>
        )}
      </Grid.Col>
    </Grid>
  )
}
